import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtMoney, fmtPct, fmtNum, fmtDate, monthLabel } from "@/lib/format";
import { PageHeader, SectionTitle, AwaitingData } from "@/components/common/Page";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: p, isLoading } = useQuery({ queryKey: ["product", id], queryFn: async () => (await api.get(`/products/${id}`)).data });
  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan-400" /></div>;
  const pr = p.profitability;
  const hasData = pr && (pr.revenue > 0 || pr.active_customers > 0);

  return (
    <div className="ns-fade">
      <Button variant="ghost" className="mb-3 text-slate-400 hover:text-slate-100" onClick={() => navigate("/products")} data-testid="back-to-products">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Products
      </Button>
      <PageHeader title={p.name} subtitle={p.description || p.code} testId="product-detail-header">
        <StatusBadge status={p.status} label={p.status} />
      </PageHeader>

      <SectionTitle className="mb-3">Profitability · {pr ? monthLabel(pr.month) : ""}</SectionTitle>
      {hasData ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KpiCard testId="pd-customers" label="Customers" value={fmtNum(pr.customers)} accent="cyan" />
          <KpiCard testId="pd-revenue" label="Revenue" value={fmtMoney(pr.revenue)} accent="cyan" />
          <KpiCard testId="pd-cost" label="Direct Cost" value={fmtMoney(pr.direct_cost)} accent="amber" />
          <KpiCard testId="pd-profit" label="Profit" value={fmtMoney(pr.profit)} accent={pr.profit >= 0 ? "emerald" : "rose"} />
          <KpiCard testId="pd-margin" label="Margin" value={fmtPct(pr.margin_pct)} accent={pr.margin_pct >= 0 ? "emerald" : "rose"} />
        </div>
      ) : (
        <AwaitingData label="Awaiting data — no active customers or costs recorded for this product yet" />
      )}

      <div className="mt-6 rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-5">
        <SectionTitle className="mb-4">Details</SectionTitle>
        <div className="grid grid-cols-2 gap-y-3 sm:grid-cols-3">
          <Field l="Code" v={p.code} />
          <Field l="Type" v={p.product_type} />
          <Field l="Status" v={p.status} />
          <Field l="Launch Date" v={fmtDate(p.launch_date)} />
          <Field l="Customers" v={fmtNum(p.customer_count)} />
        </div>
        {p.notes && <p className="mt-4 border-t border-[#1E2D4A] pt-3 text-sm text-slate-400">{p.notes}</p>}
      </div>
    </div>
  );
}

const Field = ({ l, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
    <div className="text-sm capitalize text-slate-200">{v || "—"}</div>
  </div>
);
